import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Shield, Loader, AlertCircle, CheckCircle, XCircle, Sparkles,
  ArrowLeft, ArrowRight, Rocket, Plus, X, Link as LinkIcon,
  Wand2, RotateCcw, ChevronRight, Pencil,
} from 'lucide-react';
import { supabase, TABLES, toDb } from '../api/db';
import { useAuth } from '../contexts/AuthContext';
import { TaskStatus } from '../types';
import { generateTaskCode } from '../utils/taskCodeGenerator';
import UniversePicker from '../components/UniversePicker';
import ToolPicker from '../components/ToolPicker';
import TimelinePicker from '../components/TimelinePicker';
import {
  TASK_UNIVERSES,
  EXPERIENCE_LEVELS,
  COLLAB_STYLES,
  INITIAL_FORM,
  POPULAR_TOOLS,
  type NewTaskForm,
  type TaskUniverse,
  type TaskExperience,
  type TaskTimeline,
  type CollabStyle,
  type ToolSelection,
} from '../constants/taskCreation';
import { mapNewTaskToLegacy } from '../utils/taskMapping';
import {
  parseTaskFromPrompt,
  geminiResultToForm,
  isGeminiConfigured,
  fallbackParseFromPrompt,
  type GeminiTaskResult,
} from '../api/geminiTaskParser';
import { normalizeTaskLinks, normalizeTaskTools } from '../utils/taskRecords';

// ─── Example prompts shown as chips ──────────────────────────────────────────
const EXAMPLE_PROMPTS = [
  'Build a React dashboard with Firebase auth and real-time charts',
  'Design a logo and brand kit for a fintech startup',
  'Automate my weekly email reports using n8n',
  'Create a 60-second explainer video for my SaaS product',
  'Set up a CI/CD pipeline on GitHub Actions with Docker',
  'Write SEO blog posts about AI tools for developers',
];

// ─── Phase type ───────────────────────────────────────────────────────────────
type Phase = 'prompt' | 'review' | 'manual';

const TaskHatch = () => {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editTaskId = searchParams.get('edit');

  // ── Core form state ────────────────────────────────────────────────
  const [form, setForm] = useState<NewTaskForm>({ ...INITIAL_FORM });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromReferrer, setFromReferrer] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [step, setStep] = useState(1);
  const [tagInput, setTagInput] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);

  // ── AI / Phase state ───────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>('prompt');
  const [userPrompt, setUserPrompt] = useState('');
  const [geminiLoading, setGeminiLoading] = useState(false);
  const [geminiError, setGeminiError] = useState('');
  const [geminiResult, setGeminiResult] = useState<GeminiTaskResult | null>(null);
  const [selectedSuggestedTools, setSelectedSuggestedTools] = useState<string[]>([]);

  const totalSteps = 3;

  // ── Load task for editing ──────────────────────────────────────────
  useEffect(() => {
    const loadTaskForEdit = async () => {
      if (!editTaskId || !currentUser) return;
      try {
        const { data: taskRow } = await supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('*').eq('id', editTaskId).maybeSingle();
        if (!taskRow) return;
        const d = taskRow as Record<string, unknown>;

        let hasAccess = d['user_id'] === currentUser.uid;
        if (!hasAccess) {
          const { data: connRows } = await supabase
            .from(TABLES.CONNECTION_REQUESTS)
            .select('id')
            .eq('task_id', editTaskId)
            .eq('tasker_id', currentUser.uid)
            .eq('status', 'accepted')
            .limit(1);
          hasAccess = (connRows ?? []).length > 0;
        }
        if (!hasAccess) {
          setError('You do not have permission to edit this task.');
          navigate('/task-dashboard');
          return;
        }

        setIsEditing(true);
        // Skip AI prompt phase when editing
        setPhase('manual');

        const universeFromCategory: Record<string, TaskUniverse> = {
          graphic: 'design', code: 'build', IT: 'manage', infrastructure: 'automate',
          database: 'manage', security: 'manage', network: 'manage', other: 'other',
          'login-setup': 'build',
        };
        const expFromDifficulty: Record<string, TaskExperience> = {
          beginner: 'explorer', intermediate: 'maker', advanced: 'pro', expert: 'expert',
        };

        const aiToolsList: string[] = Array.isArray(d['ai_tools_used']) ? d['ai_tools_used'] as string[] : [];
        const normalizedTools = normalizeTaskTools(d['tools']);
        const rawDeadline = d['due_date'] as string | null | undefined;

        setForm({
          universe: (d['universe'] as TaskUniverse) || universeFromCategory[d['category'] as string] || 'other',
          title: (d['title'] as string) || '',
          goal: (d['summary'] as string) || (d['goal'] as string) || '',
          experience: (d['experience'] as TaskExperience) || expFromDifficulty[d['difficulty'] as string] || 'maker',
          tools: normalizedTools.length > 0 ? normalizedTools : aiToolsList.map((name: string) => ({
            id: `custom-${name.toLowerCase().replace(/\s+/g, '-')}`,
            name,
            category: 'other',
            isCustom: true,
          })),
          description: (d['description'] as string) || (d['requirements'] as string) || '',
          links: normalizeTaskLinks(d['links']).length > 0 ? normalizeTaskLinks(d['links']) : normalizeTaskLinks(d['related_links']),
          tags: Array.isArray(d['tags']) ? d['tags'] as string[] : ((d['tags'] as string) || '').split(',').map((t: string) => t.trim()).filter(Boolean),
          timeline: (d['timeline'] as TaskTimeline) || 'this-week',
          customDeadline: rawDeadline ? new Date(rawDeadline).toISOString().split('T')[0] : '',
          visibility: ((d['visibility'] as string) === 'private' ? 'private' : 'public') as 'public' | 'private',
          collabStyle: (d['collab_style'] as CollabStyle) || 'help-wanted',
          saveToDashboard: true,
        });
      } catch (err: any) {
        console.error('Error loading task for edit:', err);
        setError('Failed to load task for editing.');
      }
    };
    loadTaskForEdit();
  }, [editTaskId, currentUser, navigate]);

  // ── Prefill from an external referrer (e.g. 3rd Eye Feel) ───────────
  // Optional query params: title, goal, description, universe. Safe to
  // ignore — the user still reviews and submits this form themselves.
  useEffect(() => {
    if (editTaskId) return;
    const title = searchParams.get('title');
    const goal = searchParams.get('goal');
    const description = searchParams.get('description');
    const universeParam = searchParams.get('universe') as TaskUniverse | null;
    if (!title && !goal && !description) return;

    const validUniverse = TASK_UNIVERSES.some((u) => u.id === universeParam) ? (universeParam as TaskUniverse) : undefined;

    setForm((prev) => ({
      ...prev,
      title: title || prev.title,
      goal: goal || prev.goal,
      description: description || prev.description,
      universe: validUniverse || prev.universe,
    }));
    setFromReferrer(true);
    setPhase('manual');
  }, [editTaskId, searchParams]);

  // ── Readiness score ────────────────────────────────────────────────
  const readiness = useMemo(() => {
    let s = 0;
    if (form.universe) s += 15;
    if (form.title.trim()) s += 20;
    if (form.goal.trim()) s += 20;
    if (form.tools.length > 0) s += 15;
    if (form.description.trim()) s += 15;
    if (form.timeline) s += 10;
    if (form.tags.length > 0) s += 5;
    return s;
  }, [form]);

  const activeUniverse = TASK_UNIVERSES.find(u => u.id === form.universe);

  // ── Tag management ─────────────────────────────────────────────────
  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !form.tags.includes(tag)) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, tag] }));
    }
    setTagInput('');
  };
  const removeTag = (tag: string) => setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));

  // ── Link management ────────────────────────────────────────────────
  const addLink = () => {
    if (linkTitle.trim() && linkUrl.trim()) {
      setForm(prev => ({ ...prev, links: [...prev.links, { title: linkTitle, url: linkUrl }] }));
      setLinkTitle('');
      setLinkUrl('');
    }
  };
  const removeLink = (i: number) => setForm(prev => ({ ...prev, links: prev.links.filter((_, idx) => idx !== i) }));

  // ── Step validation (manual mode) ──────────────────────────────────
  const canProceed = (s: number) => {
    if (s === 1) return form.universe !== '' && form.title.trim() !== '' && form.goal.trim() !== '';
    if (s === 2) return form.description.trim() !== '';
    return true;
  };
  const nextStep = () => { if (step < totalSteps && canProceed(step)) setStep(step + 1); };
  const prevStep = () => { if (step > 1) setStep(step - 1); };

  // ── Create task from prompt (AI-enriched or fallback) ──────────────
  const handleAnalyzeWithAI = async () => {
    if (!userPrompt.trim() || !currentUser) return;
    setGeminiLoading(true);
    setGeminiError('');

    let parsed: GeminiTaskResult;
    try {
      parsed = await parseTaskFromPrompt(userPrompt.trim());
    } catch (err: any) {
      console.warn('Gemini unavailable, using local fallback:', err?.message);
      parsed = fallbackParseFromPrompt(userPrompt.trim());
    }

    setGeminiResult(parsed);
    const partial = geminiResultToForm(parsed);
    const filledForm: NewTaskForm = { ...INITIAL_FORM, ...partial };

    const toolsToAdd: ToolSelection[] = (parsed.suggestedTools || []).map(name => {
      const found = POPULAR_TOOLS.find(t => t.name.toLowerCase() === name.toLowerCase());
      return found
        ? { id: found.id, name: found.name, category: found.category }
        : { id: `custom-${name.toLowerCase().replace(/\s+/g, '-')}`, name, category: 'other', isCustom: true };
    });
    filledForm.tools = toolsToAdd;

    setForm(filledForm);

    try {
      const legacy = mapNewTaskToLegacy(filledForm);
      const taskCode = generateTaskCode();

      const now = new Date().toISOString();
      const taskData = toDb({
        universe: filledForm.universe || 'other',
        title: filledForm.title,
        goal: filledForm.goal,
        experience: filledForm.experience,
        tools: filledForm.tools,
        description: filledForm.description,
        links: filledForm.links,
        timeline: filledForm.timeline,
        visibility: filledForm.visibility,
        collabStyle: filledForm.collabStyle,
        category: legacy.category,
        priority: legacy.priority,
        urgency: legacy.urgency,
        difficulty: legacy.difficulty,
        summary: legacy.summary,
        requirements: legacy.requirements,
        acceptanceCriteria: legacy.acceptanceCriteria,
        aiToolsUsed: legacy.aiToolsUsed,
        relatedLinks: legacy.relatedLinks,
        tags: legacy.tags,
        impact: legacy.impact,
        environment: legacy.environment,
        riskLevel: legacy.riskLevel,
        dataSensitivity: legacy.dataSensitivity,
        readinessScore: filledForm.title && filledForm.goal ? 55 : 30,
        status: 'intake',
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || 'Anonymous',
        assignedUsers: [currentUser.uid],
        dueDate: filledForm.customDeadline ? new Date(filledForm.customDeadline).toISOString() : null,
        updatedAt: now,
        taskCode: taskCode,
        createdAt: now,
      });

      const { data: inserted, error: insertErr } = await supabase
        .from(TABLES.TASK_HATCH_SUBMISSIONS)
        .insert(taskData)
        .select('id')
        .single();
      if (insertErr) throw insertErr;
      const taskId = (inserted as { id: string }).id;

      if (filledForm.saveToDashboard !== false) {
        await supabase.from(TABLES.TASKS).upsert({
          id: taskId,
          ...taskData,
          user_id: currentUser.uid,
          status: 'intake' as TaskStatus,
        });
      }

      setSuccess(true);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);

      navigate(`/tasks/${taskId}`);
    } catch (submitErr: any) {
      console.error('Task creation error:', submitErr);
      setGeminiError(submitErr.message || 'Failed to save task. Please try again.');
    } finally {
      setGeminiLoading(false);
    }
  };

  // ── Gemini: apply suggested tools to form ─────────────────────────
  const applySelectedTools = () => {
    const toolsToAdd: ToolSelection[] = selectedSuggestedTools.map(name => {
      const found = POPULAR_TOOLS.find(t => t.name.toLowerCase() === name.toLowerCase());
      return found
        ? { id: found.id, name: found.name, category: found.category }
        : { id: `custom-${name.toLowerCase().replace(/\s+/g, '-')}`, name, category: 'other', isCustom: true };
    });
    setForm(prev => ({ ...prev, tools: toolsToAdd }));
  };

  // ── Gemini: confirm review and go to submit ────────────────────────
  const handleConfirmReview = () => {
    applySelectedTools();
    setPhase('manual');
    setStep(3);
  };

  // ── Submit ─────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditing && step < 3) return;
    if (!currentUser) { setError('Please log in.'); return; }
    if (!form.universe || !form.title.trim() || !form.goal.trim()) {
      setError('Please complete all required fields.');
      return;
    }

    try {
      setError('');
      setSuccess(false);
      setLoading(true);

      const legacy = mapNewTaskToLegacy(form);

      const taskData = toDb({
        universe: form.universe,
        title: form.title,
        goal: form.goal,
        experience: form.experience,
        tools: form.tools,
        description: form.description,
        links: form.links,
        timeline: form.timeline,
        visibility: form.visibility,
        collabStyle: form.collabStyle,
        category: legacy.category,
        priority: legacy.priority,
        urgency: legacy.urgency,
        difficulty: legacy.difficulty,
        summary: legacy.summary,
        requirements: legacy.requirements,
        acceptanceCriteria: legacy.acceptanceCriteria,
        aiToolsUsed: legacy.aiToolsUsed,
        relatedLinks: legacy.relatedLinks,
        tags: legacy.tags,
        impact: legacy.impact,
        environment: legacy.environment,
        riskLevel: legacy.riskLevel,
        dataSensitivity: legacy.dataSensitivity,
        readinessScore: readiness,
        status: 'intake',
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || 'Anonymous',
        assignedUsers: [currentUser.uid],
        dueDate: form.customDeadline ? new Date(form.customDeadline).toISOString() : null,
        updatedAt: new Date().toISOString(),
      });

      let savedTaskId: string;

      if (isEditing && editTaskId) {
        const { error: updErr } = await supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).update(taskData).eq('id', editTaskId);
        if (updErr) throw updErr;
        savedTaskId = editTaskId;
        if (form.saveToDashboard) {
          await supabase.from(TABLES.TASKS).upsert({
            id: editTaskId,
            ...taskData,
            user_id: currentUser.uid,
            status: 'intake' as TaskStatus,
          });
        }
      } else {
        const taskCode = generateTaskCode();
        const now = new Date().toISOString();
        const { data: ins, error: insErr } = await supabase
          .from(TABLES.TASK_HATCH_SUBMISSIONS)
          .insert({ ...taskData, task_code: taskCode, created_at: now })
          .select('id')
          .single();
        if (insErr) throw insErr;
        savedTaskId = (ins as { id: string }).id;
        if (form.saveToDashboard) {
          await supabase.from(TABLES.TASKS).upsert({
            id: savedTaskId,
            ...taskData,
            task_code: taskCode,
            user_id: currentUser.uid,
            status: 'intake' as TaskStatus,
            created_at: now,
          });
        }
      }

      setSuccess(true);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);

      setTimeout(() => navigate('/task-dashboard'), 2500);
    } catch (err: any) {
      setError(err.message || 'Unable to submit task. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Auth gate ──────────────────────────────────────────────────────
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-tech-dark text-tech-text flex items-center justify-center px-4 bg-tech-mesh">
        <div className="max-w-xl tech-card p-8 rounded-2xl text-center">
          <Shield className="w-12 h-12 text-tech-cyan mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Create a Task</h1>
          <p className="text-tech-text-muted mb-4">Sign in to start creating and tracking your tasks.</p>
          <Link
            to="/login"
            className="inline-block px-5 py-3 rounded-lg font-semibold text-tech-dark focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
            style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  const stepLabels = ['What', 'How', 'Launch'];
  const nextLabels = ['Next: Add your tools', 'Next: Set timeline', 'Launch Task'];

  // ════════════════════════════════════════════════════════════════════
  // PHASE A — PROMPT INPUT
  // ════════════════════════════════════════════════════════════════════
  if (phase === 'prompt' && !isEditing) {
    return (
      <div className="min-h-screen bg-tech-dark text-tech-text bg-tech-mesh">
        {showConfetti && (
          <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center">
            <div className="text-6xl animate-bounce">🚀</div>
          </div>
        )}

        <div className="relative z-10 max-w-3xl mx-auto px-4 py-10 md:py-16">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-xs font-semibold mb-5">
              <Wand2 className="w-3.5 h-3.5" />
              Powered by Gemini AI
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-tech-text mb-3">
              Describe your task
            </h1>
            <p className="text-tech-text-muted text-base sm:text-lg max-w-xl mx-auto">
              Describe what you're working on — your task will be created instantly. You can always refine details later in the Task Wizard.
            </p>
          </div>

          {!isGeminiConfigured() && (
            <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 text-sm text-amber-100">
              <strong className="text-amber-200">Gemini API key missing.</strong>{' '}
              Add <code className="text-amber-100/90">REACT_APP_GEMINI_API_KEY</code> to{' '}
              <code className="text-amber-100/90">.env</code> at the project root, then restart{' '}
              <code className="text-amber-100/90">npm start</code>.
            </div>
          )}

          {/* Gemini error */}
          {geminiError && (
            <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-2xl p-4 flex items-start gap-3 animate-fade-in">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-200">{geminiError}</p>
              </div>
              <button onClick={() => setGeminiError('')} className="text-red-400 hover:text-red-300 shrink-0">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Prompt textarea */}
          <div className="relative mb-4">
            <textarea
              value={userPrompt}
              onChange={e => setUserPrompt(e.target.value)}
              rows={7}
              placeholder={`Describe your task in plain English...\n\ne.g. "I need a React developer to build a dashboard with Firebase auth and real-time charts. The design should be modern and dark-themed. This is for a B2B SaaS product targeting DevOps teams. Timeline is 2 weeks."`}
              className="w-full px-5 py-4 rounded-2xl bg-[#0d1117] border border-tech-border text-tech-text text-sm sm:text-base placeholder-tech-text-muted/40 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500/50 transition-all resize-none font-mono leading-relaxed"
              disabled={geminiLoading}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handleAnalyzeWithAI();
                }
              }}
            />
            <span className="absolute bottom-3 right-4 text-[10px] text-tech-text-muted/40 pointer-events-none">
              Ctrl+Enter to create
            </span>
          </div>

          {/* Example prompts */}
          <div className="mb-8">
            <p className="text-xs text-tech-text-muted/60 mb-2.5 pl-1">Try an example:</p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_PROMPTS.map((ex, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setUserPrompt(ex)}
                  disabled={geminiLoading}
                  className="px-3 py-1.5 rounded-lg bg-tech-gray/60 border border-tech-border/50 text-xs text-tech-text-muted hover:border-violet-500/40 hover:text-tech-text hover:bg-violet-500/5 transition-all disabled:opacity-40"
                >
                  {ex.length > 45 ? ex.slice(0, 45) + '…' : ex}
                </button>
              ))}
            </div>
          </div>

          {/* CTA row */}
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <button
              type="button"
              onClick={handleAnalyzeWithAI}
              disabled={geminiLoading || !userPrompt.trim()}
              className="w-full sm:w-auto flex items-center justify-center gap-2.5 px-8 py-4 rounded-2xl text-base font-bold transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: geminiLoading || !userPrompt.trim()
                  ? undefined
                  : 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)',
                color: '#fff',
                boxShadow: geminiLoading || !userPrompt.trim() ? undefined : '0 0 32px rgba(0,217,255,0.4)',
              }}
            >
              {geminiLoading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Creating your task...
                </>
              ) : (
                <>
                  <Rocket className="w-5 h-5" />
                  Create Task
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => setPhase('manual')}
              disabled={geminiLoading}
              className="text-sm text-tech-text-muted hover:text-tech-cyan transition-colors flex items-center gap-1.5 disabled:opacity-40"
            >
              <Pencil className="w-3.5 h-3.5" />
              Fill manually instead
            </button>
          </div>

          {/* Loading state detail */}
          {geminiLoading && (
            <div className="mt-6 text-center animate-fade-in">
              <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-violet-500/10 border border-violet-500/20 text-violet-300 text-sm">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                Parsing task structure with Gemini 1.5 Flash...
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // PHASE B — AI REVIEW PANEL
  // ════════════════════════════════════════════════════════════════════
  if (phase === 'review' && geminiResult && !isEditing) {
    const reviewUniverse = TASK_UNIVERSES.find(u => u.id === form.universe);

    return (
      <div className="min-h-screen bg-tech-dark text-tech-text bg-tech-mesh">
        <div className="relative z-10 max-w-3xl mx-auto px-4 py-8 md:py-12">

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <button
              type="button"
              onClick={() => setPhase('prompt')}
              className="flex items-center gap-2 text-tech-text-muted hover:text-tech-cyan transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>

          <div className="flex items-start gap-4 mb-8">
            <div className="p-2.5 rounded-xl bg-violet-500/15 border border-violet-500/30 shrink-0">
              <Wand2 className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-tech-text">AI parsed your task</h2>
              <p className="text-sm text-tech-text-muted mt-0.5">Review and adjust the fields below, then launch.</p>
            </div>
            <button
              type="button"
              onClick={() => setPhase('prompt')}
              className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg border border-tech-border text-xs text-tech-text-muted hover:border-tech-cyan/30 hover:text-tech-cyan transition-all shrink-0"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Re-analyze
            </button>
          </div>

          <div className="space-y-5">
            {/* Title */}
            <section className="tech-card rounded-2xl p-5 border border-tech-border/40 space-y-2">
              <label className="block text-xs font-bold text-tech-text-muted uppercase tracking-wider">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-tech-gray border border-tech-border text-tech-text text-base focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 focus:border-tech-cyan transition-all"
              />
            </section>

            {/* Goal */}
            <section className="tech-card rounded-2xl p-5 border border-tech-border/40 space-y-2">
              <label className="block text-xs font-bold text-tech-text-muted uppercase tracking-wider">Goal / Outcome</label>
              <textarea
                value={form.goal}
                onChange={e => setForm(prev => ({ ...prev, goal: e.target.value }))}
                rows={2}
                className="w-full px-4 py-3 rounded-xl bg-tech-gray border border-tech-border text-tech-text text-sm focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 focus:border-tech-cyan transition-all resize-none"
              />
            </section>

            {/* Universe */}
            <section className="tech-card rounded-2xl p-5 border border-tech-border/40 space-y-3">
              <label className="block text-xs font-bold text-tech-text-muted uppercase tracking-wider">Universe</label>
              <UniversePicker
                selected={form.universe as TaskUniverse}
                onSelect={(id) => setForm(prev => ({ ...prev, universe: id }))}
              />
            </section>

            {/* Description */}
            <section className="tech-card rounded-2xl p-5 border border-tech-border/40 space-y-2">
              <label className="block text-xs font-bold text-tech-text-muted uppercase tracking-wider">Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
                placeholder={reviewUniverse?.placeholder || 'Describe your task in detail...'}
                className="w-full px-4 py-3 rounded-xl bg-tech-gray border border-tech-border text-tech-text text-sm focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 focus:border-tech-cyan transition-all resize-none"
              />
            </section>

            {/* Tags */}
            <section className="tech-card rounded-2xl p-5 border border-tech-border/40 space-y-3">
              <label className="block text-xs font-bold text-tech-text-muted uppercase tracking-wider">Tags</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-tech-cyan/10 border border-tech-cyan/20 text-xs text-tech-cyan font-medium">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="hover:opacity-70"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  placeholder="Add a tag..."
                  className="flex-1 px-3 py-2 rounded-xl bg-tech-gray border border-tech-border text-sm text-tech-text placeholder-tech-text-muted/40 focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 transition-all"
                />
                <button
                  type="button"
                  onClick={addTag}
                  disabled={!tagInput.trim()}
                  className="px-3 py-2 rounded-xl bg-tech-cyan/10 border border-tech-cyan/30 text-tech-cyan text-sm hover:bg-tech-cyan/20 transition-all disabled:opacity-30"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </section>

            {/* Suggested tools */}
            {geminiResult.suggestedTools.length > 0 && (
              <section className="tech-card rounded-2xl p-5 border border-tech-border/40 space-y-3">
                <div>
                  <label className="block text-xs font-bold text-tech-text-muted uppercase tracking-wider mb-0.5">Suggested Tools</label>
                  <p className="text-[11px] text-tech-text-muted/60">Click to toggle. Selected tools will be added to your task.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {geminiResult.suggestedTools.map(tool => {
                    const selected = selectedSuggestedTools.includes(tool);
                    return (
                      <button
                        key={tool}
                        type="button"
                        onClick={() => setSelectedSuggestedTools(prev =>
                          selected ? prev.filter(t => t !== tool) : [...prev, tool]
                        )}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                          selected
                            ? 'bg-tech-cyan/15 border-tech-cyan/40 text-tech-cyan'
                            : 'bg-tech-gray border-tech-border text-tech-text-muted hover:border-tech-cyan/30'
                        }`}
                      >
                        {selected && <span className="mr-1">✓</span>}{tool}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Timeline */}
            <section className="tech-card rounded-2xl p-5 border border-tech-border/40 space-y-3">
              <label className="block text-xs font-bold text-tech-text-muted uppercase tracking-wider">Timeline</label>
              <TimelinePicker
                selected={form.timeline}
                customDeadline={form.customDeadline}
                onSelect={(id) => setForm(prev => ({ ...prev, timeline: id }))}
                onDeadlineChange={(d) => setForm(prev => ({ ...prev, customDeadline: d }))}
              />
            </section>

            {/* Experience + Collab row */}
            <div className="grid sm:grid-cols-2 gap-5">
              <section className="tech-card rounded-2xl p-5 border border-tech-border/40 space-y-3">
                <label className="block text-xs font-bold text-tech-text-muted uppercase tracking-wider">Your Level</label>
                <div className="grid grid-cols-2 gap-2">
                  {EXPERIENCE_LEVELS.map(lvl => {
                    const isActive = form.experience === lvl.id;
                    return (
                      <button
                        key={lvl.id}
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, experience: lvl.id }))}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 text-center transition-all ${
                          isActive
                            ? 'border-tech-cyan bg-tech-cyan/10 shadow-tech-glow'
                            : 'border-tech-border/50 bg-tech-gray/40 hover:border-tech-cyan/30'
                        }`}
                      >
                        <span className="text-xl">{lvl.emoji}</span>
                        <span className={`text-[10px] font-bold ${isActive ? 'text-tech-cyan' : 'text-tech-text'}`}>{lvl.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="tech-card rounded-2xl p-5 border border-tech-border/40 space-y-3">
                <label className="block text-xs font-bold text-tech-text-muted uppercase tracking-wider">Collaboration</label>
                <div className="grid grid-cols-2 gap-2">
                  {COLLAB_STYLES.map(cs => {
                    const isActive = form.collabStyle === cs.id;
                    return (
                      <button
                        key={cs.id}
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, collabStyle: cs.id }))}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 text-center transition-all ${
                          isActive
                            ? 'border-tech-cyan bg-tech-cyan/10 shadow-tech-glow'
                            : 'border-tech-border/50 bg-tech-gray/40 hover:border-tech-cyan/30'
                        }`}
                      >
                        <span className="text-xl">{cs.emoji}</span>
                        <span className={`text-[10px] font-bold ${isActive ? 'text-tech-cyan' : 'text-tech-text'}`}>{cs.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>

            {/* Confirm button */}
            <div className="flex items-center gap-4 pt-2">
              <button
                type="button"
                onClick={handleConfirmReview}
                disabled={!form.title.trim() || !form.goal.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-8 py-4 rounded-2xl text-base font-bold transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)',
                  color: '#0a0a0a',
                  boxShadow: '0 0 28px rgba(0,255,136,0.3)',
                }}
              >
                <CheckCircle className="w-5 h-5" />
                Looks good, Launch Task →
              </button>
              <button
                type="button"
                onClick={() => { setPhase('manual'); setStep(1); }}
                className="px-4 py-4 rounded-2xl border border-tech-border text-sm text-tech-text-muted hover:border-tech-cyan/30 hover:text-tech-cyan transition-all whitespace-nowrap"
              >
                Edit manually
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // PHASE C — MANUAL FORM (original 3-step form, also the submit phase)
  // ════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-tech-dark text-tech-text relative overflow-hidden bg-tech-mesh">
      {showConfetti && (
        <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center">
          <div className="text-6xl animate-bounce">
            {activeUniverse?.emoji || '🚀'}
          </div>
        </div>
      )}

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8 md:py-12">
        {fromReferrer && (
          <div className="mb-6 flex items-center gap-2 px-4 py-3 rounded-xl border border-violet-400/30 bg-violet-500/10 text-violet-200 text-sm">
            <span>✦</span>
            <span>Continuing your task from 3rd Eye Feel — review the details below and submit whenever you're ready.</span>
          </div>
        )}
        {/* ── Header ─────────────────────────────────────────────── */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              {/* Back to AI prompt (only when not editing and came from prompt flow) */}
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => { setPhase('prompt'); setStep(1); }}
                  className="flex items-center gap-1.5 text-xs text-tech-text-muted hover:text-violet-400 transition-colors mb-3"
                >
                  <Wand2 className="w-3 h-3" /> Back to AI prompt
                </button>
              )}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-tech-text">
                {isEditing ? 'Edit Task' : 'What are you working on?'}
              </h1>
              <p className="text-tech-text-muted text-sm mt-1.5">
                {isEditing
                  ? 'Update your task details'
                  : 'Tell us what you\'re building, creating, or learning — we\'ll help you make it happen.'}
              </p>
            </div>
            {/* Readiness pill */}
            <div className="hidden sm:flex items-center gap-2 bg-tech-gray/60 rounded-xl px-4 py-2.5 border border-tech-border/50">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                style={{ background: `conic-gradient(#22d3ee ${readiness * 3.6}deg, rgba(255,255,255,0.06) 0deg)` }}
              >
                <span className="bg-tech-dark rounded-full w-7 h-7 flex items-center justify-center text-xs">{readiness}</span>
              </div>
              <span className="text-xs text-tech-text-muted">Ready</span>
            </div>
          </div>

          {/* ── Progress bar ─────────────────────────────────────── */}
          {!isEditing && (
            <div className="flex items-center gap-2">
              {stepLabels.map((label, i) => {
                const s = i + 1;
                const isActive = step === s;
                const isDone = step > s;
                return (
                  <React.Fragment key={s}>
                    <button
                      type="button"
                      onClick={() => (isDone || isActive) && setStep(s)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                        isActive
                          ? 'bg-tech-cyan/15 border border-tech-cyan/40 text-tech-cyan shadow-tech-glow'
                          : isDone
                            ? 'bg-tech-accent/10 border border-tech-accent/30 text-tech-accent cursor-pointer'
                            : 'border border-tech-border/40 text-tech-text-muted'
                      }`}
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        isDone ? 'bg-tech-accent text-tech-dark' : isActive ? 'bg-tech-cyan/20' : 'bg-tech-gray'
                      }`}>
                        {isDone ? '✓' : s}
                      </span>
                      {label}
                    </button>
                    {i < stepLabels.length - 1 && (
                      <div className={`flex-1 h-0.5 rounded-full transition-colors duration-500 ${
                        step > s ? 'bg-tech-accent/50' : 'bg-tech-border/30'
                      }`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </header>

        {/* ── Error / Success messages ───────────────────────────── */}
        {error && (
          <div className="mb-6 animate-fade-in bg-red-500/10 border border-red-500/30 rounded-2xl p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-200 flex-1">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-300"><XCircle className="w-4 h-4" /></button>
          </div>
        )}

        {success && (
          <div className="mb-6 animate-fade-in bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <p className="text-sm text-emerald-200">
              {isEditing ? 'Task updated successfully!' : 'Task created! Redirecting to your dashboard...'}
            </p>
          </div>
        )}

        {/* ── Form ───────────────────────────────────────────────── */}
        <form
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA' && step < totalSteps) {
              e.preventDefault();
            }
          }}
        >
          {/* ═══════════ STEP 1: WHAT ═══════════ */}
          {(step === 1 || isEditing) && (
            <div className={`space-y-6 ${!isEditing ? 'animate-fade-in' : ''}`}>
              <section className="tech-card rounded-2xl p-5 md:p-7 border border-tech-border/40">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-tech-cyan" />
                  <h2 className="text-lg font-semibold">Pick your universe</h2>
                </div>
                <UniversePicker
                  selected={form.universe as TaskUniverse}
                  onSelect={(id) => setForm(prev => ({ ...prev, universe: id }))}
                />
              </section>

              <section className="tech-card rounded-2xl p-5 md:p-7 border border-tech-border/40 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-tech-text mb-2">
                    Give your task a name <span className="text-amber-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder={activeUniverse?.examples[0] ? `e.g. ${activeUniverse.examples[0]}` : "What would you tell a friend you're working on?"}
                    className="w-full px-4 py-3.5 rounded-xl bg-tech-gray border border-tech-border text-tech-text text-base placeholder-tech-text-muted/40 focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 focus:border-tech-cyan transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-tech-text mb-2">
                    What's the end result? <span className="text-amber-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.goal}
                    onChange={e => setForm(prev => ({ ...prev, goal: e.target.value }))}
                    placeholder="When this is done, what will exist that doesn't exist now?"
                    className="w-full px-4 py-3.5 rounded-xl bg-tech-gray border border-tech-border text-tech-text text-base placeholder-tech-text-muted/40 focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 focus:border-tech-cyan transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-tech-text mb-3">Your level</label>
                  <div className="flex flex-wrap gap-2">
                    {EXPERIENCE_LEVELS.map(lvl => {
                      const isActive = form.experience === lvl.id;
                      return (
                        <button
                          key={lvl.id}
                          type="button"
                          onClick={() => setForm(prev => ({ ...prev, experience: lvl.id }))}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all duration-200 ${
                            isActive
                              ? 'border-tech-cyan bg-tech-cyan/10 text-tech-cyan shadow-tech-glow'
                              : 'border-tech-border/50 text-tech-text-muted hover:border-tech-cyan/30'
                          }`}
                        >
                          <span className="text-lg">{lvl.emoji}</span>
                          <div className="text-left">
                            <div className="font-bold text-xs">{lvl.label}</div>
                            <div className="text-[10px] opacity-70">{lvl.desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* ═══════════ STEP 2: HOW ═══════════ */}
          {(step === 2 || isEditing) && (
            <div className={`space-y-6 ${!isEditing ? 'animate-fade-in' : 'mt-6'}`}>
              <section className="tech-card rounded-2xl p-5 md:p-7 border border-tech-border/40">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-tech-cyan" />
                  <h2 className="text-lg font-semibold">Tools you're using</h2>
                </div>
                <p className="text-xs text-tech-text-muted mb-4">Select the apps, AI tools, and platforms powering this task.</p>
                <ToolPicker
                  selected={form.tools}
                  onChange={(tools) => setForm(prev => ({ ...prev, tools }))}
                />
              </section>

              <section className="tech-card rounded-2xl p-5 md:p-7 border border-tech-border/40 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-tech-text mb-2">
                    Describe what needs to happen <span className="text-amber-400">*</span>
                  </label>
                  <textarea
                    value={form.description}
                    onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={activeUniverse?.placeholder || 'Describe your task in detail...'}
                    rows={5}
                    className="w-full px-4 py-3.5 rounded-xl bg-tech-gray border border-tech-border text-tech-text text-sm placeholder-tech-text-muted/40 focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 focus:border-tech-cyan transition-all resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-tech-text mb-2">Tags</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {form.tags.map(tag => (
                      <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-tech-cyan/10 border border-tech-cyan/20 text-xs text-tech-cyan font-medium animate-fade-in">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)} className="hover:opacity-70"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                      placeholder="Type a tag and press Enter"
                      className="flex-1 px-3 py-2 rounded-xl bg-tech-gray border border-tech-border text-sm text-tech-text placeholder-tech-text-muted/40 focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-tech-text mb-2">
                    Links & resources <span className="text-tech-text-muted font-normal">(optional)</span>
                  </label>
                  {form.links.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {form.links.map((link, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-tech-gray/60 border border-tech-border/50 text-xs animate-fade-in">
                          <LinkIcon className="w-3.5 h-3.5 text-tech-cyan shrink-0" />
                          <span className="font-medium text-tech-text truncate">{link.title}</span>
                          <span className="text-tech-text-muted truncate flex-1">{link.url}</span>
                          <button type="button" onClick={() => removeLink(i)} className="text-red-400 hover:text-red-300"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={linkTitle}
                      onChange={e => setLinkTitle(e.target.value)}
                      placeholder="Title"
                      className="flex-1 px-3 py-2 rounded-xl bg-tech-gray border border-tech-border text-sm text-tech-text placeholder-tech-text-muted/40 focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 transition-all"
                    />
                    <input
                      type="url"
                      value={linkUrl}
                      onChange={e => setLinkUrl(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }}
                      placeholder="https://..."
                      className="flex-1 px-3 py-2 rounded-xl bg-tech-gray border border-tech-border text-sm text-tech-text placeholder-tech-text-muted/40 focus:outline-none focus:ring-2 focus:ring-tech-cyan/40 transition-all"
                    />
                    <button
                      type="button"
                      onClick={addLink}
                      disabled={!linkTitle.trim() || !linkUrl.trim()}
                      className="px-3 py-2 rounded-xl bg-tech-cyan/10 border border-tech-cyan/30 text-tech-cyan text-sm font-medium hover:bg-tech-cyan/20 transition-all disabled:opacity-30"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* ═══════════ STEP 3: LAUNCH ═══════════ */}
          {(step === 3 || isEditing) && (
            <div className={`space-y-6 ${!isEditing ? 'animate-fade-in' : 'mt-6'}`}>
              <section className="tech-card rounded-2xl p-5 md:p-7 border border-tech-border/40">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-tech-cyan" />
                  <h2 className="text-lg font-semibold">When do you need this done?</h2>
                </div>
                <TimelinePicker
                  selected={form.timeline}
                  customDeadline={form.customDeadline}
                  onSelect={(id) => setForm(prev => ({ ...prev, timeline: id }))}
                  onDeadlineChange={(d) => setForm(prev => ({ ...prev, customDeadline: d }))}
                />
              </section>

              <section className="tech-card rounded-2xl p-5 md:p-7 border border-tech-border/40">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-semibold">How do you want to work on this?</h2>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {COLLAB_STYLES.map(cs => {
                    const isActive = form.collabStyle === cs.id;
                    return (
                      <button
                        key={cs.id}
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, collabStyle: cs.id }))}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 text-center ${
                          isActive
                            ? 'border-tech-cyan bg-tech-cyan/10 shadow-tech-glow'
                            : 'border-tech-border/50 bg-tech-gray/40 hover:border-tech-cyan/30'
                        }`}
                      >
                        <span className="text-2xl">{cs.emoji}</span>
                        <span className={`text-xs font-bold ${isActive ? 'text-tech-cyan' : 'text-tech-text'}`}>{cs.label}</span>
                        <span className="text-[10px] text-tech-text-muted">{cs.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="tech-card rounded-2xl p-5 md:p-7 border border-tech-border/40">
                <h2 className="text-lg font-semibold mb-4">Who can see this task?</h2>
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { id: 'public' as const, emoji: '🌍', label: 'Public', desc: 'Anyone on Continuum can find this and offer to help' },
                    { id: 'private' as const, emoji: '🔒', label: 'Just Me', desc: 'Only you can see this — track it privately' },
                  ]).map(v => {
                    const isActive = form.visibility === v.id;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, visibility: v.id }))}
                        className={`flex flex-col items-center gap-2 p-5 rounded-xl border-2 transition-all duration-200 text-center ${
                          isActive
                            ? 'border-tech-cyan bg-tech-cyan/10 shadow-tech-glow'
                            : 'border-tech-border/50 bg-tech-gray/40 hover:border-tech-cyan/30'
                        }`}
                      >
                        <span className="text-3xl">{v.emoji}</span>
                        <span className={`text-sm font-bold ${isActive ? 'text-tech-cyan' : 'text-tech-text'}`}>{v.label}</span>
                        <span className="text-xs text-tech-text-muted">{v.desc}</span>
                      </button>
                    );
                  })}
                </div>

                <label className="flex items-center gap-3 mt-5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={form.saveToDashboard}
                    onChange={e => setForm(prev => ({ ...prev, saveToDashboard: e.target.checked }))}
                    className="w-5 h-5 rounded border-tech-border bg-tech-gray text-tech-cyan focus:ring-tech-cyan/40 accent-cyan-400"
                  />
                  <span className="text-sm text-tech-text-muted group-hover:text-tech-text transition-colors">
                    Add to my Task Hub — track progress, get matched, show on profile
                  </span>
                </label>
              </section>

              {/* Task Preview */}
              {form.title && (
                <section className="tech-card rounded-2xl p-5 md:p-7 border border-tech-cyan/20 bg-gradient-to-br from-tech-cyan/5 to-transparent">
                  <h3 className="text-sm font-semibold text-tech-cyan mb-3 flex items-center gap-2">
                    <Rocket className="w-4 h-4" /> Task Preview
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{activeUniverse?.emoji}</span>
                      <h4 className="text-lg font-bold text-tech-text">{form.title}</h4>
                    </div>
                    <p className="text-sm text-tech-text-muted">{form.goal}</p>
                    {form.tools.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {form.tools.map(t => (
                          <span key={t.id} className="px-2 py-0.5 rounded-md bg-tech-gray text-[10px] text-tech-text-muted border border-tech-border/50">
                            {t.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {form.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {form.tags.map(tag => (
                          <span key={tag} className="text-[10px] text-tech-cyan/80">#{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>
          )}

          {/* ── Navigation buttons ────────────────────────────────── */}
          <div className="flex items-center justify-between mt-8 gap-4">
            {!isEditing && step > 1 ? (
              <button
                type="button"
                onClick={prevStep}
                className="flex items-center gap-2 px-5 py-3 rounded-xl border border-tech-border text-tech-text-muted hover:text-tech-text hover:border-tech-cyan/40 transition-all text-sm font-medium"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : (
              <div />
            )}

            {!isEditing && step < totalSteps ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={!canProceed(step)}
                className="flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-bold transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  background: canProceed(step) ? 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' : undefined,
                  color: canProceed(step) ? '#0a0a0a' : undefined,
                  boxShadow: canProceed(step) ? '0 0 24px rgba(0,102,255,0.3)' : undefined,
                }}
              >
                {nextLabels[step - 1]}
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading || !form.title.trim() || !form.goal.trim()}
                className="flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-bold transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)',
                  color: '#0a0a0a',
                  boxShadow: '0 0 24px rgba(0,255,136,0.3)',
                }}
              >
                {loading ? (
                  <><Loader className="w-4 h-4 animate-spin" /> Submitting...</>
                ) : (
                  <><Rocket className="w-4 h-4" /> {isEditing ? 'Save Changes' : 'Launch Task'}</>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskHatch;
